// Grid detail page — composes stats / range-bar / levels / orders / trades.
import { useParams } from "react-router-dom";
import { useGridDetail } from "@/hooks/use-grid-detail";
import { useGridPnl } from "@/hooks/use-grid-pnl";
import { useLivePrice } from "@/providers/ws-provider";
import GridStats from "@/components/grid/grid-stats";
import GridRangeBar from "@/components/grid/grid-range-bar";
import GridLevelsView from "@/components/grid/grid-levels-view";
import GridOrdersTable from "@/components/grid/grid-orders-table";
import GridTradesTable from "@/components/grid/grid-trades-table";
import GridActions from "@/components/grid/grid-actions";
import AiAdviceCard from "@/components/ai/ai-advice-card";
import { useDetailActions } from "@/components/shell/detail-layout";

export default function GridDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error } = useGridDetail(id ?? "");
  const { data: pnl } = useGridPnl(id ?? "");
  const livePrice = useLivePrice(data?.grid.symbol ?? "");

  // Mount action buttons into DetailLayout's sticky sub-header slot.
  useDetailActions(data ? <GridActions grid={data.grid} /> : null);

  if (!id) return <div className="text-danger text-sm p-4">Invalid grid ID</div>;

  if (isLoading) {
    return <div className="text-muted text-sm text-center py-12">Loading grid…</div>;
  }

  if (error || !data) {
    return (
      <div className="text-danger text-sm text-center py-8">
        Failed to load grid: {(error as Error | null)?.message ?? "Unknown error"}
      </div>
    );
  }

  const { grid, orders, trades, position, unmatched_openings: unmatchedOpenings, volatility } = data;

  return (
    <div>

      {/* 6-stat header */}
      <GridStats
        grid={grid}
        pnl={pnl}
        position={position}
        unmatchedLegs={unmatchedOpenings}
        livePrice={livePrice}
        volatility={volatility}
      />

      {/* Range bar — only when we have a live price */}
      <GridRangeBar grid={grid} orders={orders} livePrice={livePrice} />

      {/* Grid levels dot view */}
      <div className="mb-4">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted mb-2">
          Levels
        </h3>
        <GridLevelsView
          grid={grid}
          orders={orders}
          unmatchedOpenings={unmatchedOpenings}
          livePrice={livePrice}
        />
      </div>

      {/* AI Advisor */}
      <AiAdviceCard
        entityType="grid"
        entityId={grid.id}
        aiEnabled={Boolean(grid.ai_enabled)}
        lastAdvice={grid.ai_advice}
      />

      {/* Orders table */}
      <GridOrdersTable
        orders={orders}
        symbol={grid.symbol}
        unmatchedOpenings={unmatchedOpenings}
        maxHoldMinutes={grid.max_hold_minutes}
      />

      {/* Trades table */}
      <GridTradesTable
        gridId={grid.id}
        trades={trades}
        orders={orders}
        symbol={grid.symbol}
        maxHoldMinutes={grid.max_hold_minutes}
        livePrice={livePrice}
      />
    </div>
  );
}
